// Un solo botón "Desprogramar", compartido por las pantallas que enseñan un
// saludo en verde: Inicio (Saludos y Felicitaciones), Alertas y Eventos del
// mes. Se dibuja SOLO cuando hay algo pendiente en la cola que cancelar — si
// el saludo ya salió, no hay nada que deshacer y el botón no aparece.
//
// Sirve para las dos formas de saludo, y por eso el texto lo pone él y no cada
// pantalla: un cumpleaños es un correo (o dos, si el cónyuge tiene el suyo) y
// un feriado son TODOS los de la cartera. Decir "el correo quedará cancelado"
// cuando son trescientos no es un matiz de redacción — es lo que hace que se
// pulse sin saber lo que se está deshaciendo.

import { useState } from 'react';
import { Undo2 } from 'lucide-react';
import { confirmDialog } from './ui.jsx';
import { toast } from './Toast.jsx';

export default function UnscheduleGreetingButton({
  rows, personKey, dayKey, markerKey, who, kind = 'bday', unschedule, onDone,
  lang = 'es', compact = false,
}) {
  const es = lang === 'es';
  const [busy, setBusy] = useState(false);
  if (!rows || rows.length === 0) return null;

  const n = rows.length;
  const esFeriado = kind === 'holiday';
  // Decir CUÁNTOS y DE QUÉ: un cliente puede tener dos filas (la suya y la del
  // cónyuge con correo propio) y un feriado tiene una por cada cliente activo.
  // Cancelarlas en silencio sorprendería.
  const mensaje = esFeriado
    ? (es
      ? `Se cancelarán los ${n} correo(s) del saludo de "${who || 'este feriado'}" — uno por cliente. Nadie lo recibirá y el feriado vuelve a quedar pendiente. Puedes volver a programarlo cuando quieras.`
      : `All ${n} email(s) for the "${who || 'this holiday'}" greeting — one per client — will be cancelled. Nobody receives it and the holiday goes back to pending. You can schedule it again whenever you want.`)
    : (es
      ? `${n === 1 ? 'El correo quedará cancelado' : `Los ${n} correos quedarán cancelados`}${who ? ` para ${who}` : ''} y la tarjeta volverá a quedar pendiente. Puedes volver a programarlo cuando quieras.`
      : `${n === 1 ? 'The email will be cancelled' : `All ${n} emails will be cancelled`}${who ? ` for ${who}` : ''} and the card goes back to pending. You can schedule it again whenever you want.`);

  const click = async (e) => {
    e.stopPropagation();
    const ok = await confirmDialog({
      title: es
        ? (esFeriado ? 'Desprogramar el feriado' : 'Desprogramar el saludo')
        : (esFeriado ? 'Unschedule the holiday' : 'Unschedule the greeting'),
      message: mensaje,
      confirmLabel: es ? 'Desprogramar' : 'Unschedule',
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await unschedule({ rows, personKey, dayKey, markerKey });
      // `unschedule` devolvía un número antes de que los feriados pudieran
      // fallar a medias; se acepta cualquiera de las dos formas.
      const hechos = typeof r === 'number' ? r : (r?.cancelled ?? 0);
      const fallidos = typeof r === 'number' ? 0 : (r?.failed ?? 0);
      if (fallidos) {
        // Media verdad es peor que ninguna: lo que sigue en cola VA A SALIR, y
        // la tarjeta se queda en verde a propósito para que se vea.
        toast.warn(es
          ? `Se cancelaron ${hechos}, pero ${fallidos} no se pudieron cancelar y todavía saldrán. Míralos en Correos programados.`
          : `${hechos} cancelled, but ${fallidos} could not be cancelled and will still go out. Check them in Scheduled emails.`);
      } else {
        toast.success(es
          ? `Desprogramado — ${hechos === 1 ? 'el correo no saldrá' : `${hechos} correos no saldrán`}.`
          : `Unscheduled — ${hechos === 1 ? 'the email will not go out' : `${hechos} emails will not go out`}.`);
      }
      // La tarjeta solo vuelve a "pendiente" cuando de verdad no queda nada.
      if (!fallidos) onDone && onDone();
    } catch (err) {
      toast.error((es ? 'No se pudo desprogramar: ' : "Couldn't unschedule: ") + (err?.message || ''));
    } finally {
      setBusy(false);
    }
  };

  return (
    <button type="button" onClick={click} disabled={busy}
      title={es ? 'Cancelar el envío y volver a dejarlo pendiente' : 'Cancel the send and put it back to pending'}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0,
        padding: compact ? '3px 7px' : '4px 9px', borderRadius: 7, fontFamily: 'inherit',
        fontSize: compact ? '.62rem' : '.68rem', fontWeight: 700,
        cursor: busy ? 'wait' : 'pointer', opacity: busy ? .6 : 1,
        border: '1px solid rgba(255,183,0,.35)', background: 'rgba(255,183,0,.1)',
        color: 'var(--c-amber,#ffb700)',
      }}>
      <Undo2 size={compact ? 10 : 12} />{es ? 'Desprogramar' : 'Unschedule'}
    </button>
  );
}
